import { getErrorStatus, toError } from '~/utils/http/api-error'

export const ERROR_TOAST_OPTIONS = { autoClose: false, closeButton: true } as const

// `pending`, when supplied, is expected to be `useAsyncData`'s `pending` ref.
// In current Nuxt (4.5.1) that ref is `computed(() => status.value === 'pending')`
// (see node_modules/nuxt/dist/app/composables/asyncData.js) -- a readonly
// computed, not a writable ref -- so `useAction` can no longer reuse it
// directly as its own loading flag; writing to it warns in dev and silently
// no-ops. `useAction` now always owns its loading state in `actionLoading`
// and returns a writable computed `loading` that reads as true while either
// the action or the supplied `pending` (if any) is in flight, and whose
// writes go to `actionLoading` only. This keeps `loading.value = ...`
// working for every existing caller -- both the majority that call
// `useAction()` with no `pending` and assign to `loading` directly, and the
// few that pass `useAsyncData`'s `pending` in for a combined indicator.
export const useAction = (pending?: Ref<boolean>, error?: Ref<Error | true | null>) => {
  const { displayMessages, genericMessage } = useErrorText()
  const errors = ref<string[]>([])
  const errorObj = error ?? ref<Error>()
  const actionLoading = ref(false)
  const loading = computed({
    get: () => actionLoading.value || (pending?.value ?? false),
    set: (value: boolean) => {
      actionLoading.value = value
    }
  })
  const processAction = async (action: () => Promise<void>, callback?: () => void, showToast?: boolean, toastExcludes?: number[]) => {
    loading.value = true
    errors.value.splice(0)
    errorObj.value = null
    try {
      await action()
    } catch (e) {
      const err = toError(e, genericMessage())
      errors.value = displayMessages(e)
      errorObj.value = err
      const statusCode = getErrorStatus(e) ?? 500
      if (showToast && !toastExcludes?.includes(statusCode)) {
        // Read before the import: `errors` is shared by every action of this runner, and
        // another one can empty it while the toast chunk loads.
        const message = errors.value[0] ?? genericMessage()
        // Loaded on demand (same rationale as AssetIdBanner.vue): nuxt2's global
        // `$toast` plugin was not ported, so the equivalent is imported only on the
        // failure path rather than shipped in every route's chunk.
        const { toast } = await import("vue3-toastify")
        // Error styling/icon, as nuxt2's `$toast.error` did — a neutral toast reads
        // as success for a failed action. Kept open until dismissed, as nuxt2's
        // error snackbar was: the 5 s default can vanish before it is read.
        toast.error(message, ERROR_TOAST_OPTIONS)
      }
    } finally {
      loading.value = false
      if (errors.value.length < 1) {
        errors.value.splice(0)
        errorObj.value = null
      }
      if (callback) {
        callback()
      }
    }
  }

  return { processAction, errors, error: errorObj, loading }
}

// What a caller takes to share one `useAction` -- and so one loading flag -- with
// another half of its flow (see `useCheckoutFlow`).
export type ActionRunner = Pick<ReturnType<typeof useAction>, 'processAction' | 'loading'>
